import PropTypes from "prop-types";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { BaseForm, FormTextarea } from "components/BaseForm";
import { HoldMessageFormPreview } from "components/HoldMessagePreview";
import HoldMessageVoiceFields, {
  HOLD_MESSAGE_MAX_LENGTH,
} from "components/HoldMessageVoiceFields";
import { useHoldMessageLanguages } from "hooks/useHoldMessageLanguages";
import TwilioPhoneNumberService from "services/TwilioPhoneNumber";

const REQUIRED_FIELDS = [
  ["hold_message", "Hold message"],
  ["hold_message_language", "Language"],
  ["hold_message_voice", "Voice"],
];

// Text, language and voice are saved together; leaving all three empty
// reverts the number to the instance-wide hold message.
const schema = z
  .object({
    hold_message: z
      .string()
      .trim()
      .max(
        HOLD_MESSAGE_MAX_LENGTH,
        `Hold message must be at most ${HOLD_MESSAGE_MAX_LENGTH} characters`
      ),
    hold_message_language: z.string().nullable(),
    hold_message_voice: z.string().nullable(),
  })
  .superRefine((values, ctx) => {
    const isFilled = name => Boolean(values[name]?.trim());
    if (!REQUIRED_FIELDS.some(([name]) => isFilled(name))) return;
    REQUIRED_FIELDS.filter(([name]) => !isFilled(name)).forEach(
      ([name, label]) =>
        ctx.addIssue({
          code: "custom",
          path: [name],
          message: `${label} is required`,
        })
    );
  });

const toPayload = values => ({
  hold_message: values.hold_message.trim() || null,
  hold_message_language: values.hold_message_language || null,
  hold_message_voice: values.hold_message_voice || null,
});

const DEFAULT_HOLD_MESSAGE_PAYLOAD = {
  hold_message: null,
  hold_message_language: null,
  hold_message_voice: null,
};

const AssignHoldMessageForm = ({
  twilioPhoneNumberId,
  holdMessage,
  onSuccess,
}) => {
  const { languages, isLoading } = useHoldMessageLanguages();

  // Runs outside BaseForm's submit so a half-edited form cannot block the
  // reset and the form's onSuccess does not fire a second time.
  const resetToDefault = useMutation({
    mutationFn: () =>
      TwilioPhoneNumberService.assignHoldMessage(
        twilioPhoneNumberId,
        DEFAULT_HOLD_MESSAGE_PAYLOAD
      ),
    onSuccess: () => {
      toast.success("Hold message reset to default");
      onSuccess?.();
    },
    onError: error => toast.error(error.message),
  });

  if (isLoading) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:p-6">
        <Spinner />
      </div>
    );
  }

  const handleSubmit = async values => {
    await TwilioPhoneNumberService.assignHoldMessage(
      twilioPhoneNumberId,
      toPayload(values)
    );
  };

  const handleSuccess = () => {
    toast.success("Hold message saved");
    onSuccess?.();
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={{
        hold_message: holdMessage?.hold_message ?? "",
        hold_message_language: holdMessage?.hold_message_language ?? null,
        hold_message_voice: holdMessage?.hold_message_voice ?? null,
      }}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      submitButtonText="Save"
      extraButtons={
        holdMessage?.hold_message
          ? [
              <Button
                key="use-default"
                type="button"
                variant="outline"
                disabled={resetToDefault.isPending}
                onClick={() => resetToDefault.mutate()}
              >
                Use Default
              </Button>,
            ]
          : undefined
      }
    >
      <FormTextarea
        name="hold_message"
        label="Hold Message"
        placeholder="Using default hold message"
        rows={3}
      />
      <HoldMessageVoiceFields
        languageName="hold_message_language"
        voiceName="hold_message_voice"
        languages={languages}
      />
      <HoldMessageFormPreview
        messageName="hold_message"
        languageName="hold_message_language"
        voiceName="hold_message_voice"
      />
    </BaseForm>
  );
};

AssignHoldMessageForm.propTypes = {
  twilioPhoneNumberId: PropTypes.number.isRequired,
  holdMessage: PropTypes.shape({
    hold_message: PropTypes.string,
    hold_message_language: PropTypes.string,
    hold_message_voice: PropTypes.string,
  }),
  onSuccess: PropTypes.func.isRequired,
};

export default AssignHoldMessageForm;
